import React, { ChangeEvent, useState } from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Typography,
} from '@material-ui/core';
import { Form } from 'formik';
import { useMutation } from '@apollo/client';
import { validateBillingForm } from './validation';
import FormikWithRef from '../Generic/FormikWithRef';
import { InputErrorMessage } from '../Generic/InputErrorMessage';
import { CHECKOUT } from '../../client-api/graphql/mutations';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';
import { IBasketItem } from '../../pages/checkout/[slug]';
import CheckBoxSharp from '@material-ui/icons/CheckBoxSharp';
import CheckBoxOutlineBlankSharpIcon from '@material-ui/icons/CheckBoxOutlineBlankSharp';
import { InputTextField } from '../Generic/Forms/InputTextField';
import { ICheckoutResult } from '../../backend/types/responses';
import styled from 'styled-components';
import { getCurrentProductPrice } from '../../util/price';

const CheckoutInput = styled(InputTextField)`
  > div {
    margin: 4px 0px;
  }
  input {
    padding: 10px 12px;
    height: 1.2875em;
  }
`;

export interface IDiscountCodeApplied {
  code: string;
  discountPercentage: number;
  products: string[];
}

const dropdownStyles = {
  width: '100%',
  padding: '8px',
  margin: '4px 0px',
  border: '1px solid #ced4da',
};

const initialValues = {
  discountCode: '',
  email: '',
  firstName: '',
  lastName: '',
  company: '',
  phone: '',
  address1: '',
  address2: '',
  townCity: '',
  region: '',
  postCode: '',
  firstNameShipping: '',
  lastNameShipping: '',
  companyShipping: '',
  phoneShipping: '',
  address1Shipping: '',
  address2Shipping: '',
  townCityShipping: '',
  regionShipping: '',
  postCodeShipping: '',
};

interface IProps {
  reqId: string;
  items: IBasketItem[];
  discountCode?: string;
  onCheckoutCompleted(response: ICheckoutResult): void;
}

export const BillingDetails = React.forwardRef((props: IProps, ref) => {
  const [checkout] = useMutation<{ checkout: ICheckoutResult }>(CHECKOUT, {
    onCompleted(response) {
      props.onCheckoutCompleted(response.checkout);
    },
    onError(err) {
      alert('Checkout error: ' + err.message);
    },
  });

  const [sameAsShipping, setSameAsShipping] = useState<boolean>(true);

  const { items, discountCode } = props;

  return (
    <FormikWithRef
      ref={ref}
      initialValues={initialValues}
      validate={values => {
        const errors = validateBillingForm(values, sameAsShipping);
        return errors;
      }}
      onSubmit={async (values, { setSubmitting }) => {
        const billingAddress = {
          firstName: values.firstName,
          lastName: values.lastName,
          companyName: values.company,
          address1: values.address1,
          address2: values.address2,
          postalCode: values.postCode,
          phoneNumber: values.phone,
          city: values.townCity,
          state: values.region,
          countryCode: values.country,
        };
        const price = getCurrentProductPrice(items[0]);

        await checkout({
          variables: {
            id: props.reqId,
            purchase: {
              products: items.map(x => ({ id: x._id, quantity: x.quantity })),
              discountCode: !!discountCode ? discountCode : undefined,
              price: {
                amount: price.amount,
                currency: price.currency,
              },
              customer: {
                email: values.email,
                billingAddress,
                deliveryAddress: sameAsShipping
                  ? billingAddress
                  : {
                      firstName: values.firstNameShipping,
                      lastName: values.lastNameShipping,
                      companyName: values.companyShipping,
                      address1: values.address1Shipping,
                      address2: values.address2Shipping,
                      postalCode: values.postCodeShipping,
                      phoneNumber: values.phoneShipping,
                      city: values.townCityShipping,
                      state: values.regionShipping,
                      countryCode: values.countryShipping,
                    },
              },
            },
          },
        });
        setSubmitting(false);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleSubmit,
        submitCount,
        handleChange,
        handleBlur,
        isSubmitting,
      }) => {
        return (
          <Paper>
            <Box px={3} py={2}>
              <Grid container justifyContent="space-between" alignItems="center">
                <Typography style={{ fontWeight: 'bold' }}>Billing details</Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      data-testid="shipping-same-checkbox"
                      icon={<CheckBoxOutlineBlankSharpIcon />}
                      checkedIcon={<CheckBoxSharp />}
                      checked={sameAsShipping}
                      onChange={() => setSameAsShipping(!sameAsShipping)}
                    />
                  }
                  style={{ fontSize: 14 }}
                  label="Same as shipping details"
                />
              </Grid>
              <Form onSubmit={handleSubmit} onChange={() => {}}>
                <CheckoutInput fullWidth placeholder="Email address" name="email" type="text" />
                <CheckoutInput fullWidth placeholder="First Name" name="firstName" type="text" />
                <CheckoutInput fullWidth placeholder="Last Name" name="lastName" type="text" />
                <CheckoutInput fullWidth placeholder="Company" name="company" type="text" />
                <CheckoutInput fullWidth placeholder="Phone Number" name="phone" type="text" />
                <CheckoutInput fullWidth placeholder="Address 1" name="address1" type="text" />
                <CheckoutInput fullWidth placeholder="Address 2" name="address2" type="text" />
                <CountryDropdown
                  data-testid="country-dropdown-billing"
                  //@ts-ignore
                  style={dropdownStyles as any}
                  priorityOptions={['GB', 'US']}
                  name="country"
                  valueType="short"
                  disabled={isSubmitting}
                  value={values.country}
                  onChange={((_: any, e: ChangeEvent) => handleChange(e)) as any}
                  onBlur={handleBlur}
                />
                {errors.country && (touched.country || !!submitCount) && (
                  <InputErrorMessage>Please select your country</InputErrorMessage>
                )}
                <RegionDropdown
                  data-testid="region-dropdown-billing"
                  //@ts-ignore
                  style={dropdownStyles as any}
                  name="region"
                  countryValueType="short"
                  country={values.country}
                  disabled={isSubmitting as any}
                  value={values.region}
                  onChange={((_: any, e: ChangeEvent) => handleChange(e)) as any}
                  onBlur={handleBlur as any}
                />
                {errors.region && (touched.region || !!submitCount) && (
                  <InputErrorMessage>Please selection your region</InputErrorMessage>
                )}
                <CheckoutInput fullWidth placeholder="Town / City" name="townCity" type="text" />
                <CheckoutInput fullWidth placeholder="Post code" name="postCode" type="text" />
                {!sameAsShipping ? (
                  <>
                    <Divider style={{ margin: '12px 0px' }} />
                    <Typography style={{ fontWeight: 'bold' }}>Shipping details</Typography>
                    <CheckoutInput
                      fullWidth
                      placeholder="First Name"
                      name="firstNameShipping"
                      type="text"
                    />
                    <CheckoutInput
                      fullWidth
                      placeholder="Last Name"
                      name="lastNameShipping"
                      type="text"
                    />
                    <CheckoutInput
                      fullWidth
                      placeholder="Company"
                      name="companyShipping"
                      type="text"
                    />
                    <CheckoutInput
                      fullWidth
                      placeholder="Phone Number"
                      name="phoneShipping"
                      type="text"
                    />
                    <CheckoutInput
                      fullWidth
                      placeholder="Address 1"
                      name="address1Shipping"
                      type="text"
                    />
                    <CheckoutInput
                      fullWidth
                      placeholder="Address 2"
                      name="address2Shipping"
                      type="text"
                    />
                    <CountryDropdown
                      //@ts-ignore
                      style={dropdownStyles as any}
                      priorityOptions={['GB', 'US']}
                      disabled={isSubmitting}
                      name="countryShipping"
                      valueType="short"
                      value={values.countryShipping}
                      onChange={((_: any, e: ChangeEvent) => handleChange(e)) as any}
                      onBlur={handleBlur}
                    />
                    {errors.countryShipping && (touched.countryShipping || !!submitCount) && (
                      <InputErrorMessage>Bad input</InputErrorMessage>
                    )}
                    <RegionDropdown
                      //@ts-ignore
                      style={dropdownStyles as any}
                      disabled={isSubmitting as any}
                      name="regionShipping"
                      countryValueType="short"
                      country={values.countryShipping}
                      value={values.regionShipping}
                      onChange={((_: any, e: ChangeEvent) => handleChange(e)) as any}
                      onBlur={handleBlur as any}
                    />
                    {errors.regionShipping && (touched.regionShipping || !!submitCount) && (
                      <InputErrorMessage>Bad input</InputErrorMessage>
                    )}
                    <CheckoutInput
                      fullWidth
                      placeholder="Town / City"
                      name="townCityShipping"
                      type="text"
                    />
                    <CheckoutInput
                      fullWidth
                      placeholder="Post code"
                      name="postCodeShipping"
                      type="text"
                    />
                  </>
                ) : null}
              </Form>
            </Box>
          </Paper>
        );
      }}
    </FormikWithRef>
  );
});

export default BillingDetails;
